import { BehaviorSubject } from 'rxjs';
import { v4 } from 'uuid';
import { InventoryAsset, OutsideInventorySchema, Product, AddressInfo } from '../types';
import { FormStateStatus } from './BulkForm';
import {
  FileValidationResponse,
  createOutsideInventory,
  updateOutsideInventory,
  uploadXLFileValidation
} from '../Api/OutsideInventory/outsideInventoryApi';
import {
  validateAssetMigration as validateAssetMigrationApi,
  migrateAssets as migrateAssetsApi
} from '../Api/OutsideInventory/assetMigrationApi';
import { getSupplierById } from './Supplier';
import { addError } from './Error';
import { ProgressHandler } from '../Api/Attachments/attachmentsApi';
import { geoCodeAddress } from '../utils/helpers';
import { downloadBlob } from '../utils/fileDownload';
import { callExternalApi, CallResult } from '../Api/callExternalApi';

export interface AssetFieldMapping {
  assetField: string;
  assetFieldKey: string;
  portalField: string;
  portalFieldKey: string;
  required: boolean;
}

export interface AssetMigrationRecord {
  id: string;
  external_id: string;
  account_id: string;
  supplier_id: string;
  product_id: string;
  outside_inventory_schema_id: string;
  content: InventoryAsset;
}

export interface AssetMigrationError {
  asset_id: string;
  asset_number: string;
  error: string;
}

export interface AssetMigrationValidationResponse {
  valid_count: number;
  error_count: number;
  errors: AssetMigrationError[];
  valid_asset_ids: string[];
  preview: AssetMigrationRecord[];
}

export interface AssetMigrationResult {
  created: AssetMigrationRecord[];
  failed: AssetMigrationError[];
}

const emitErrorState = (message?: string): void => addError('outside-inventory', message);

export interface OutsideInventoryFormProps {
  dirty: boolean;
  valid: boolean;
  id: string;
  formData: InventoryAsset;
}

export interface CreateOutsideInventoryState {
  id: string;
  status: FormStateStatus;
  error?: string;
  data?: Partial<InventoryAsset>;
  requestBody: InventoryAsset;
}

export const defaultOutsideInventoryForm = (): OutsideInventoryFormProps => {
  const id = v4();

  return {
    id,
    dirty: false,
    valid: false,
    formData: {
      address: '',
      billingFrequency: '',
      cageRackID: '',
      circuitIDs: '',
      circuitType: '',
      completionDate: '',
      serviceActivationDate: '',
      connectivityA: '',
      connectivityB: '',
      contractEndDate: '',
      disconnectedDate: '',
      equipmentManufacturer: '',
      equipmentType: '',
      firstInvoiceReviewedByDate: '',
      id: id,
      sfId: '',
      iPDetails: '',
      kwRack: 0,
      lecCircuitID: '',
      lecDemarc: '',
      accessProvider: '',
      locationContact: {
        name: '',
        phone: '',
        email: ''
      },
      locationALecCircuitId: '',
      locationALecDemarc: '',
      locationALocalLec: '',
      locationAText: '',
      locationId: '',
      locationZLecCircuitId: '',
      locationZLecDemarc: '',
      locationZLocalLec: '',
      locationZText: '',
      mRC: 0,
      nRC: 0,
      orderType: '',
      phoneNumbers: [],
      product: {
        id: '',
        name: '',
        family: '',
        productCode: ''
      },
      providerAccountNumber: '',
      supplierOrderNumber: '',
      productSupportNumber: '',
      supplierSupportNumber: '',
      rackType: '',
      status: 'Active',
      supplier: {
        id: '',
        name: ''
      },
      term: '',
      equipmentDetails: '',
      disconnectedReason: '',
      accessType: '',
      addressInfo: {
        address: '',
        locationName: '',
        city: '',
        state: '',
        zip: '',
        country: '',
        latitude: 0,
        longitude: 0,
        number: ''
      },
      outside_inventory_schema_id: '',
      account_id: '',
      product_id: '',
      supplier_id: null,
      showAdvanced: false,
      locationAId: '',
      locationZId: ''
    }
  };
};

const outsideInventoryFormSubject = new BehaviorSubject<OutsideInventoryFormProps[]>([defaultOutsideInventoryForm()]);
const outsideInventoryCreateStateSubject = new BehaviorSubject<CreateOutsideInventoryState[]>([]);
const outsideInventoryFormValidSubject = new BehaviorSubject<boolean>(false);
const outsideInventoryValidationStateSubject = new BehaviorSubject<FileValidationResponse>(
  {} as FileValidationResponse
);

export const getOutsideInventoryValidationStateSubject = (): BehaviorSubject<FileValidationResponse> =>
  outsideInventoryValidationStateSubject;

export const getOutsideinventoryFormSubject = (): BehaviorSubject<OutsideInventoryFormProps[]> =>
  outsideInventoryFormSubject;

export const getOutsideInventoryCreateStateSubject = (): BehaviorSubject<CreateOutsideInventoryState[]> =>
  outsideInventoryCreateStateSubject;

export const getOutsideInventoryFormValidSubject = (): BehaviorSubject<boolean> => outsideInventoryFormValidSubject;

export const clearForm = () => {
  outsideInventoryFormSubject.next([defaultOutsideInventoryForm()]);
  checkFormsValid();
};

export const clearFormCreateSubject = () => outsideInventoryCreateStateSubject.next([]);

export const addForm = () => {
  const currentValue = [...outsideInventoryFormSubject.getValue()];
  const newForm = defaultOutsideInventoryForm();
  outsideInventoryFormSubject.next([...currentValue, newForm]);
  checkFormsValid();
};

export const removeForm = (id: string) => {
  const update = [...outsideInventoryFormSubject.getValue()];
  const index = update.findIndex((item) => item?.id === id);
  if (index > -1) update.splice(index, 1);
  outsideInventoryFormSubject.next(update);
  checkFormsValid();
};

export const updateForm = async (id: string, data: Partial<InventoryAsset>, dirty = true, valid = false) => {
  const update = [...outsideInventoryFormSubject.getValue()];
  const index = update.findIndex((form) => form.id === id);
  if (index > -1) {
    const updatedFormData = { ...update[index].formData, ...(data as InventoryAsset) };
    if (
      updatedFormData?.supplier_id &&
      (!updatedFormData?.supplier.id || updatedFormData?.supplier.id !== updatedFormData.supplier_id)
    ) {
      const matchedSupplier = getSupplierById(updatedFormData.supplier_id);
      updatedFormData.supplier = {
        id: matchedSupplier?.id || updatedFormData?.supplier_id,
        name: matchedSupplier?.name || updatedFormData?.supplier_id
      };
    }
    if (updatedFormData?.address && !updatedFormData.addressInfo.latitude && !updatedFormData.addressInfo.longitude) {
      const geoCodedAddressInfo = await createGeoCodedOutsideInventoryAddress(updatedFormData.address);
      if (geoCodedAddressInfo) {
        updatedFormData.address = geoCodedAddressInfo.locationName;
        updatedFormData.addressInfo = {
          ...updatedFormData.addressInfo,
          ...geoCodedAddressInfo
        };
      }
    }

    update[index] = {
      ...update[index],
      formData: updatedFormData,
      dirty: dirty,
      valid: valid
    };

    outsideInventoryFormSubject.next(update);

    checkFormsValid();
  }
};

export const submitCreateForm = async (): Promise<CreateOutsideInventoryState[]> => {
  const forms = outsideInventoryFormSubject.getValue();
  const creationState: CreateOutsideInventoryState[] = [];
  const promises = forms.map(async (form) => {
    const result = await createOutsideInventory(form.formData);
    const reqState: CreateOutsideInventoryState = {
      id: form.id,
      status: result.data ? FormStateStatus.SUCCESS : FormStateStatus.SUBMITTING,
      error: result.error?.message,
      data: result.data || undefined,
      requestBody: form.formData
    };
    creationState.push(reqState);
  });
  await Promise.all(promises);
  outsideInventoryCreateStateSubject.next(creationState);
  return creationState;
};

export const resubmitIndividualitem = async (id: string, body: InventoryAsset) => {
  const result = await createOutsideInventory(body);
  const update = [...outsideInventoryCreateStateSubject.getValue()];
  const index = update.findIndex((state) => state.id === id);

  if (index > -1) {
    update[index] = {
      id: id,
      status: result.data ? FormStateStatus.SUCCESS : FormStateStatus.FAILED,
      error: result.error?.message,
      data: result.data || undefined,
      requestBody: body
    };
  }

  outsideInventoryCreateStateSubject.next(update);
};

export const setFormValidity = (id: string, valid: boolean) => {
  outsideInventoryFormSubject.next(
    outsideInventoryFormSubject.getValue().map((form) => (form.id === id ? { ...form, valid } : form))
  );
  checkFormsValid();
};

export const initUpdateForm = (formData: InventoryAsset) => {
  const updateForm: OutsideInventoryFormProps = {
    id: v4(),
    dirty: false,
    valid: true,
    formData: { ...formData, showAdvanced: true }
  };
  outsideInventoryFormSubject.next([updateForm]);
  checkFormsValid();
};

export const submitUpdateForm = async (supportType = false): Promise<CreateOutsideInventoryState[]> => {
  const forms = outsideInventoryFormSubject.getValue();
  const creationState: CreateOutsideInventoryState[] = [];
  const promises = forms.map(async (form) => {
    const result = await updateOutsideInventory(form.formData.id, form.formData, supportType);
    const reqState: CreateOutsideInventoryState = {
      id: form.id,
      status: result.data ? FormStateStatus.SUCCESS : FormStateStatus.FAILED,
      error: result.error?.message,
      data: result.data || undefined,
      requestBody: form.formData
    };
    creationState.push(reqState);
  });
  await Promise.all(promises);
  outsideInventoryCreateStateSubject.next(creationState);
  return creationState;
};

export const updateFormProduct = (
  formId: string,
  product: Product,
  allSchemas: OutsideInventorySchema[],
  accountId?: string
): OutsideInventorySchema | undefined => {
  const forms = outsideInventoryFormSubject.getValue();
  const form = forms.find((state) => state.id === formId);

  if (!form) return;
  const { formData } = form;

  const matchedSchema = allSchemas
    .filter((schema) => schema.product_id === product.id)
    .reduce(
      (latestVersion, schema): OutsideInventorySchema => {
        if (schema.version > latestVersion.version) return schema;
        return latestVersion;
      },
      { version: 0 } as OutsideInventorySchema
    );
  if (matchedSchema) {
    const update = { ...formData };
    update.product_id = product.id;
    update.product = { ...product };
    update.outside_inventory_schema_id = matchedSchema.id;
    update.account_id = accountId;
    updateForm(formId, update, false);
    return matchedSchema;
  }
};

const checkFormsValid = () => {
  const forms = [...outsideInventoryFormSubject.getValue()];
  const allFormsValid = forms.every((form) => form.valid);
  outsideInventoryFormValidSubject.next(allFormsValid);
};

export const downloadTemplate = async () => {
  const { data, error } = await callExternalApi<Blob>({
    url: 'api/oi/template',
    method: 'GET',
    responseType: 'blob'
  });

  if (error) {
    emitErrorState(error.message);
    return;
  }

  if (data) downloadBlob(data, 'template.xlsx');
};

export const uploadFileValidation = async (file: File, progressHandler: ProgressHandler, accountId: string) => {
  const { data, error } = await uploadXLFileValidation(file, progressHandler, accountId);
  if (error) emitErrorState(error.message);
  if (data) {
    data.errors = data.errors.map((er) => ({
      ...er,
      id: v4()
    }));
    outsideInventoryValidationStateSubject.next(data);
  }
};

const findAddressComponent = (result: google.maps.GeocoderResult, type: string) =>
  result.address_components.find((comp) => comp.types.includes(type));

const geoCodedState = (result: google.maps.GeocoderResult): string => {
  const state = findAddressComponent(result, 'administrative_area_level_1');
  const inUnitedStates = findAddressComponent(result, 'country')?.short_name === 'US';
  return (inUnitedStates ? state?.long_name : state?.short_name) || '';
};

export const createGeoCodedOutsideInventoryAddress = async (
  address: string
): Promise<Omit<AddressInfo, 'number'> | null> => {
  try {
    const geoCodedAddress = await geoCodeAddress(address);
    if (!geoCodedAddress) return null;

    const shortName = (type: string) => findAddressComponent(geoCodedAddress, type)?.short_name || '';

    return {
      locationName: geoCodedAddress.formatted_address,
      latitude: geoCodedAddress.geometry.location.lat(),
      longitude: geoCodedAddress.geometry.location.lng(),
      address: shortName('premise') || `${shortName('street_number')} ${shortName('route')}`.trim(),
      city: shortName('locality'),
      state: geoCodedState(geoCodedAddress),
      zip: shortName('postal_code'),
      country: shortName('country')
    };
  } catch (error) {
    return null;
  }
};

export const validateAssetMigration = async (
  file: File,
  fieldMappings: AssetFieldMapping[]
): Promise<CallResult<AssetMigrationValidationResponse>> => {
  const result = await validateAssetMigrationApi(file, fieldMappings);
  if (result.error) emitErrorState(result.error.message);
  return result;
};

export const migrateAssets = async (
  assetIds: string[],
  fieldMappings: AssetFieldMapping[]
): Promise<CallResult<AssetMigrationResult>> => {
  const result = await migrateAssetsApi(assetIds, fieldMappings);
  if (result.error) emitErrorState(result.error.message);
  return result;
};
